/**
 * Represents a pagination item - either a page number or an ellipsis marker
 */
export type PageItem = number | "ellipsis-start" | "ellipsis-end"

/**
 * Parameters for generating pagination page numbers
 */
export interface GeneratePageNumbersParams {
   /** The currently active page (1-indexed) */
   currentPage: number
   /** Total number of pages available */
   totalPages: number
   /** Maximum number of page buttons to show (including first/last) */
   maxVisible: number
}

/**
 * Generates an array of page numbers and ellipsis markers for pagination display
 *
 * Implements a smart ellipsis pattern that always shows:
 * - First page (1)
 * - Last page (totalPages)
 * - Current page with surrounding context
 * - Ellipsis markers ('...') for gaps
 *
 * @param currentPage - The currently active page (1-indexed)
 * @param totalPages - Total number of pages available
 * @param maxVisible - Maximum number of page buttons to show (typically 5 for mobile, 7 for desktop)
 * @returns Array of page numbers and ellipsis markers
 *
 * @example
 * // Mobile with 100 pages, current page 50
 * generatePageNumbers({ currentPage: 50, totalPages: 100, maxVisible: 5 })
 * // Returns: [1, 'ellipsis-start', 50, 'ellipsis-end', 100]
 *
 * @example
 * // Desktop with 10 pages, current page 5
 * generatePageNumbers({ currentPage: 5, totalPages: 10, maxVisible: 7 })
 * // Returns: [1, 'ellipsis-start', 4, 5, 6, 'ellipsis-end', 10]
 */
export function generatePageNumbers({ currentPage, totalPages, maxVisible }: GeneratePageNumbersParams): PageItem[] {
   // Edge case: single page
   if (totalPages <= 1) return [1]

   // Small total: show all pages (no ellipsis needed)
   if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
   }

   // Calculate how many pages to show around current page
   // Reserve 2 slots for first and last page, 1 for current
   const sidePages = maxVisible - 3
   const leftSiblings = Math.floor(sidePages / 2)
   const rightSiblings = Math.ceil(sidePages / 2)

   // Determine if we need ellipsis on left/right
   // Show ellipsis only if there's a gap of more than 1 page
   const showLeftEllipsis = currentPage - leftSiblings > 2
   const showRightEllipsis = currentPage + rightSiblings < totalPages - 1

   const pages: PageItem[] = []

   // Always show first page
   pages.push(1)

   // Left section (between first page and current)
   if (showLeftEllipsis) {
      pages.push("ellipsis-start")
      // Show pages around current
      const start = Math.max(2, currentPage - leftSiblings)
      const end = currentPage
      for (let i = start; i <= end; i++) {
         if (i < totalPages) pages.push(i)
      }
   } else {
      // Show consecutive pages from 2 to current
      for (let i = 2; i <= currentPage; i++) {
         if (i < totalPages) pages.push(i)
      }
   }

   // Right section (after current page to last)
   if (showRightEllipsis) {
      // Show pages after current
      const start = currentPage + 1
      const end = Math.min(totalPages - 1, currentPage + rightSiblings)
      for (let i = start; i <= end; i++) {
         if (i > 1) pages.push(i)
      }
      pages.push("ellipsis-end")
   } else {
      // Show remaining consecutive pages up to last
      for (let i = currentPage + 1; i < totalPages; i++) {
         pages.push(i)
      }
   }

   // Always show last page (if more than 1 page total)
   if (totalPages > 1) pages.push(totalPages)

   return pages
}
